import { FC, useMemo } from "react"
import { useFormikContext, getIn, FieldArray } from "formik"

import { KTSVG } from "../../../../../../../../_metronic/helpers"
import { cloneDeep } from "lodash"

import { initDOCLabelItemValues } from "../core"
import { useDOCLabelFormView } from "./DOCLabelFormContextProvider"
import { FormikTextInput, FormikSelect, FormikSwitch } from "../../../../../../../components/widgets"
import { LEAF_TYPE_OPTIONS } from "../../../../../../core/doclabel"


type Props = {
  siteIndex: number
}

export const DOCLabelItemDetailsTable:FC<Props> = ({siteIndex}) => {

  const { isDoor, initValues } = useDOCLabelFormView();    
  const { requestMode } = initValues;

  const { values, setFieldValue}  = useFormikContext();

  const { itemFieldName, siteItemDetails } = useMemo(() => {
    if (! (values && siteIndex > -1)) return { siteItemDetails : null, itemFieldName : '' };

    const siteDetails = getIn(values, 'siteDetails');
    if (! (siteDetails && siteDetails.length > 0)) return { siteItemDetails : null, itemFieldName : '', total_labels: 0 };
    
    const itemFieldName = `siteDetails.${siteIndex}.itemDetails`;
    const siteItemDetails = getIn(values, itemFieldName);
    return { itemFieldName, siteItemDetails };
  }, [values, siteIndex]);

  const parseDoorSize = (e: any) => {
    const elem = e.target.closest('.itemRow');
    if (!(elem && elem?.dataset)) return;
    
    const idx = elem.dataset.idx;
    const _itemBase = `siteDetails.${siteIndex}.itemDetails.${idx}.size`;
    const F = e.target.name.match(/sizeF$/) ? e.target.value : getIn(values, `${_itemBase}F`);
    const L = e.target.name.match(/sizeL$/) ? e.target.value : getIn(values, `${_itemBase}L`);        
    setFieldValue(_itemBase,`${F ? F : ''}(F) ${L ? L : ''}(L)`);
  }

  if (! itemFieldName) {
    return null;
  }

  return (
  <FieldArray
    name={itemFieldName}
    render={arrayHelpers => (
    <>
    <div className='col-9 fw-bold text-muted fs-6 required mt-2'>
      Item Details
    </div>
    <div className='separator border-gray-200 my-0'></div>
    <div className='table-responsive min-h-80px mh-150px'>
      <table
        id='kt_table_dlmapplication_itemdetails'
        className='table align-middle table-row-bordered table-row-gray-300 fs-7 gy-2 dataTable no-footer'
      >
        <thead>
          <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0 gx-2'>
            <th className="p-2">No</th>
            <th className="p-2 required">Size W x H  (mm) / Model</th>
            <th className="p-2 required">Quantity</th>
            { isDoor &&
            <>
            <th className="p-2 required">Leaf Type</th>
            <th className="p-2 required">Vision Panel</th>
            </>
            }
            <th className="p-2">Remarks</th>
            <th className="p-2 text-end">
              {(! ['edit_site','edit_app'].includes(requestMode)) && 
              <>
              { (!(siteItemDetails && siteItemDetails.length > 0)) &&
              <span className="text-muted fs-7 fw-normal text-lowercase me-2">Click to add items →</span>
              }
              <button
                  type="button"
                className="siteBtn btn btn-light-success text-success btn-active-success px-2 py-1 lh-0"
                onClick={()=> {
                  arrayHelpers.push({
                    ...cloneDeep(initDOCLabelItemValues),
                    isDoor
                  });
                }}
                title="Click to Add Item"
              >
                <KTSVG path='/media/icons/duotune/arrows/arr017.svg' className='svg-icon m-0' />
              </button>
              </>
              }
            </th>
          </tr>
        </thead>
        { (siteItemDetails && siteItemDetails.length > 0) &&
        <tbody className='text-gray-800 align-middle fs-6'>
          {
          [...Array(siteItemDetails.length)].map((_ , idx) => (
          <tr
            key={`siteItemDetail${siteIndex}_${idx}`}
            className="itemRow"
            data-idx={idx}
          >
            <td className="p-2 align-top">
              <div className="text-muted fs-7 fw-bold w-30px">{idx + 1}</div>
            </td>
            <td className="p-2 align-top">
              { isDoor ? (
              <>
              <div className="row align-items-top w-300px">
                <FormikTextInput
                  size={'5 pe-0'}
                  name={`${itemFieldName}.${idx}.sizeF`}
                  onChange={parseDoorSize}
                  addClass="fs-7"
                  placeholder="W x H"
                />
                <div className="col-1 ps-0 fw-bold fs-7">(F)</div>
                <FormikTextInput
                  size={'5'}
                  name={`${itemFieldName}.${idx}.sizeL`}
                  onChange={parseDoorSize}
                  addClass="fs-7"
                  placeholder="W x H"
                />
                <div className="col-1 ps-0 fw-bold fs-7">(L)</div>                                
              </div>
              </>
              ) : (
              <FormikTextInput
                size={12}
                name={`${itemFieldName}.${idx}.size`}
                addClass={'w-150px fs-7'}
                placeholder="W x H"
              />
              )}

            </td>
            <td className="p-2 align-top">
              <FormikTextInput
                size={12}
                name={`${itemFieldName}.${idx}.quantity`}
                decimals={0}
                addClass={'w-90px fs-7'}
                readOnly={['resubmit','resubmit_site','edit','edit_site','edit_app'].includes(requestMode)}
              />
            </td>
            { isDoor &&
            <>
            <td className="p-2 align-top min-w-100px">
              <FormikSelect
                size={12}
                name={`${itemFieldName}.${idx}.doorType`}                                
                options={LEAF_TYPE_OPTIONS}
              />
            </td>
            <td className="p-2 align-top text-center">
              <FormikSwitch
                size={'12'}
                name={`${itemFieldName}.${idx}.visionPanel`}
              />
            </td>
            </>
            }
            <td className="p-2 align-top min-w-100px">
              <FormikTextInput
                size={12}
                name={`${itemFieldName}.${idx}.remarks`}                                
              />
            </td>
            <td className="p-2 align-top text-end">
              <button
                type="button"
                className="siteBtn btn btn-light-danger text-success btn-active-danger px-2 py-1 lh-0"
                onClick={() => {
                  arrayHelpers.remove(idx);
                }}
                title="Click to Remove Item"
              >
                <KTSVG path='/media/icons/duotune/arrows/arr097.svg' className='svg-icon m-0' />
              </button>
            </td>                        
          </tr>
          ))
          }
        </tbody>
        }
      </table>
    </div>
    </>
    )}
  />
  )
}